<template>
  <div class="view-adminuserlist">
    <div class="breadcrump-trail">
      <router-link :to="{ name: 'admin-home' }"> Admin </router-link>
      / <span> Nutzer </span>
    </div>

    <admin-subscription-invoices-list class="mv-4" />

    <h1>{{ $t("headings.userAdministration") }}</h1>

    <router-link :to="{ name: 'admin-users-create' }" class="button">
      Nutzer erstellen
    </router-link>

    <div class="view-adminuserlist__filters">
      <button
        v-for="filter in filters"
        :key="filter.id"
        class="view-adminuserlist__filter"
        :class="{ '-is-active': filter.id == activeFilter?.id }"
        type="button"
        @click="setFilter(filter)"
      >
        {{ filter.label }}
      </button>
    </div>

    <div v-show="settings.activeFilterId == 'search'">
      <input-text ref="filterInput" v-model:value="settings.search" label="Filter" />
    </div>

    <div class="line_flex mv-4">
      <div v-show="count > 0" class="mv-2">
        User {{ firstUserNum }} - {{ lastUserNum }} von {{ count }}
      </div>

      <pages-nav
        class="ml-auto"
        :limit="limit"
        :count="count"
        :offset="usersOffset"
        @load="loadUsers($event.offset)"
      />
    </div>

    <!-- USER TABLE -->
    <table class="view-adminuserlist__table">
      <thead>
        <tr>
          <th @click="setOrdering('email')">
            <span>{{ $t("labels.email") }}</span>
            <span v-if="settings.ordering == 'email'" class="ml-2">
              <template v-if="settings.orderDescending">▼</template>
              <template v-else>▲</template>
            </span>
          </th>
          <th @click="setOrdering('firstname,lastname,city')">
            <span>Kontaktdaten</span>
            <span v-if="settings.ordering == 'firstname,lastname,city'" class="ml-2">
              <template v-if="settings.orderDescending">▼</template>
              <template v-else>▲</template>
            </span>
          </th>
          <th
            @click="
              setOrdering('subscriptions_count')
            "
          >
            <span>Abonnements</span>
            <span
              v-if="
                settings.ordering == 'subscriptions_count'
              "
              class="ml-2"
            >
              <template v-if="settings.orderDescending">▼</template>
              <template v-else>▲</template>
            </span>
          </th>
          <th @click="setOrdering('max_valid_until')">
            <span>Gültig bis</span>
            <span v-if="settings.ordering == 'max_valid_until'" class="ml-2">
              <template v-if="settings.orderDescending">▼</template>
              <template v-else>▲</template>
            </span>
          </th>
          <th @click="setOrdering('organizations_count')">
            <span>{{ $t("headings.organizations") }}</span>
            <span v-if="settings.ordering == 'organizations_count'" class="ml-2">
              <template v-if="settings.orderDescending">▼</template>
              <template v-else>▲</template>
            </span>
          </th>
          <th @click="setOrdering('created')">
            <span>{{ $t("labels.created") }}</span>
            <span v-if="settings.ordering == 'created'" class="ml-2">
              <template v-if="settings.orderDescending">▼</template>
              <template v-else>▲</template>
            </span>
          </th>
          <th @click="setOrdering('last_login')">
            <span>Letzter Login</span>
            <span v-if="settings.ordering == 'last_login'" class="ml-2">
              <template v-if="settings.orderDescending">▼</template>
              <template v-else>▲</template>
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="user in users" :key="user.id">
          <td>
            <router-link
              :to="{ name: 'admin-users-update', params: { userId: user.id } }"
            >
              {{ user.email }}
            </router-link>
            <div v-if="!user.verified" class="view-adminuserlist__inactive">
              <strong>Nicht aktiviert</strong>
            </div>
            <div v-if="user.notifiedOfUpcomingDeletion" class="flex mt-1 gap-2">
              <div>💀</div>
              <div>Löschungswarnung am
              {{ formatDateString(user.notifiedOfUpcomingDeletion) }}</div>
            </div>
          </td>
          <td>
            {{ user.name }}<br />
          </td>
          <td>
            <div v-for="subscription in user.subscriptions?.filter((s) => !s.expired)" :key="subscription.id" class="mb-2">
              <strong>{{ subscription.planInfo?.label }}</strong>
              <div class="column">
                <div>{{ subscription.seats }} Nutzer</div>
                <div v-for="upgrade of subscription.upgrades">
                  {{ $t(`upgrades.${upgrade}.name`) }}
                </div>
              </div>
            </div>
          </td>
          <td>
            <template v-if="user.maxValidUntil">
              {{ formatDateString(user.maxValidUntil) }}
            </template>
          </td>
          <td>
            <ul class="view-adminuserlist__organizations mt-0">
              <li
                v-for="license in user.organizationLicenses"
                :key="license.id"
              >
                <router-link
                  class="view-adminuserlist__orglink"
                  :to="{
                    name: 'admin-organizations-detail',
                    params: {
                      organizationId: license.organizationId
                    }
                  }"
                  >{{ license.organizationName }} ({{
                    license.roleLabel
                  }})</router-link
                >
              </li>
            </ul>
          </td>
          <td>{{ formatDateString(user.created) }}</td>
          <td>{{ formatDateTime(user.lastLogin) }}</td>
        </tr>
      </tbody>
    </table>

    <pages-nav
      :limit="limit"
      :count="count"
      :offset="usersOffset"
      @load="loadUsers($event.offset)"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, reactive } from "vue";
import { debounce } from "lodash-es";
import { formatDate } from "@/i18n";

import { useAdmin } from "./useAdmin.js";

import InputText from "@core/input/InputText.vue";
import PagesNav from "./PagesNav.vue";
import AdminSubscriptionInvoicesList from "./AdminSubscriptionInvoicesList.vue";

import type { AdminUser } from "@/api/User";

const adminAPI = useAdmin();

const users = ref<AdminUser[]>([]);
const usersOffset = ref(0);
const count = ref(0);
const limit = ref(50);

const settings = reactive({
  activeFilterId: null as null | string,
  ordering: null as null | string,
  orderDescending: false,
  search: ""
});

const filterInput = ref<HTMLInputElement | null>(null);

const filters = computed(() => {
  console.log("search", settings.search);
  return [
    {
      id: "search",
      label: "Suche",
      query: {
        search: settings.search
      }
    },
    {
      id: "active",
      label: "Aktive Nutzer",
      query: {
        active: true
      }
    },
    {
      id: "inactive",
      label: "Inaktive Nutzer",
      query: {
        active: false
      }
    }
  ];
});

const activeFilter = computed(() => {
  return filters.value.find((f) => f.id === settings.activeFilterId);
});

const firstUserNum = computed(() => {
  return usersOffset.value + 1;
});

const lastUserNum = computed(() => {
  return (
    usersOffset.value + Math.min(count.value - usersOffset.value, limit.value)
  );
});


loadUsers();

const checkSearchStr = debounce(() => {
  usersOffset.value = 0;
  loadUsers();
}, 250);

watch(() => settings.search, () => {
  checkSearchStr();
});

function setOrdering(value: string) {
  if (settings.ordering == value) {
    settings.orderDescending = !settings.orderDescending;
  }
  settings.ordering = value;
  loadUsers();
}

function setFilter(filter: { id: string }) {
  if (filter.id == settings.activeFilterId) {
    settings.activeFilterId = null;
  } else {
    settings.activeFilterId = filter.id;
    if (filter.id === "search") {
      filterInput.value?.focus();
    }
  }

  usersOffset.value = 0;
  loadUsers();
}

async function loadUsers(offset = 0) {
  await loadUsersLink({
    ...(activeFilter.value?.query ?? {}),
    ordering: `${settings.orderDescending ? "-" : ""}${settings.ordering}`,
    offset: offset
  });
}

async function loadUsersLink(
  query: Record<string, string | number | undefined | boolean>,
  requestOptions = {}
) {
  const usersPage = await adminAPI.get<{
    count: number;
    limit: number;
    results: AdminUser[];
    offset: number;
  }>("/users", {
    ...requestOptions,
    query: {
      limit: limit.value,
      ...query
    },
    parseResponse: (response) => response.body as {
      count: number;
      limit: number;
      results: AdminUser[];
      offset: number;
    }
  });
  count.value = usersPage.count;
  limit.value = usersPage.limit;
  const loadedUsers = usersPage.results;
  usersOffset.value = usersPage.offset;
  users.value = loadedUsers;
  return loadedUsers;
}

function formatDateString(string?: string) {
  if (!string) return "";
  const date = new Date(string);
  return formatDate(date, "D");
}

function formatDateTime(datetimeString?: string) {
  if (!datetimeString) return "";
  const date = new Date(datetimeString);
  return formatDate(date, "f");
}
</script>

<style lang="scss">
.view-adminuserlist {
  padding: 2rem;
  max-width: 75rem;
  width: 100%;

  &__table {
    border-collapse: collapse;
    background-color: var(--color-background-deep);
    margin: 1rem 0;
    width: 100%;

    td,
    th {
      border: 1px solid var(--color-border);
      padding: 0.5rem 0.75rem;
      vertical-align: top;
    }

    tr:hover {
      background-color: var(--color-background);
    }

    thead th {
      cursor: pointer;
    }
  }

  &__address {
    font-size: 12px;
    padding-top: 0.5rem;
  }

  &__inactive {
    color: darkred;
    font-weight: 700;
    &::before {
      content: "!";
      color: red;
      font-weight: 700;
      margin-right: 0.25rem;
    }
  }

  &__userlink {
    display: block;
    padding: 0.75rem 1.5rem;
    height: 100%;
  }
  td.view-adminuserlist__userlinkcell {
    padding: 0;
    height: 100%;
  }

  &__filters {
    display: flex;
    gap: 0.5rem;
    margin-top: 2rem;
  }

  &__filter {
    border-radius: 5px;
    border: 1px solid var(--color-border);
    padding: 0.25em 0.5em;
    background-color: var(--color-background-medium);
    &.-is-active {
      color: var(--color-action-contrast);
      background-color: var(--color-action);
    }
  }

  &__organizations {
    margin: 0.5rem 0;
  }

  &__orglink {
    color: var(--color-text);
    font-weight: 400;
  }
}
</style>
